import type { Backend, RawQuery } from '@freelancer/datastore/core';
import {
  OrderByDirection,
  getQueryParamValue,
} from '@freelancer/datastore/core';
import { toNumber } from '@freelancer/utils';
import { ContestHandoverUpdateAction } from './contest-handovers.backend-model';
import type { ContestHandover } from './contest-handovers.model';
import type { ContestHandoversCollection } from './contest-handovers.types';

export function contestHandoversBackend(): Backend<ContestHandoversCollection> {
  return {
    defaultOrder: {
      field: 'id',
      direction: OrderByDirection.ASC,
    },
    fetch: (authUid, ids, query, order) => ({
      endpoint: 'contests/0.1/handovers/',
      isGaf: false,
      params: {
        users: getUsers(query),
        // TODO: T297173 Make the role SELLER if sellerId is passed in to make
        // UI tests work for multiawarded contests
        user_role:
          getQueryParamValue(query, 'buyerId').length > 0 ? 'SELLER' : 'BUYER',
        entry_ids: getQueryParamValue(query, 'entryId'),
        contests: getQueryParamValue(query, 'contestId'),
        buyer_signed_contract: getQueryParamValue(
          query,
          'buyerSignedContract',
        )[0],
        seller_signed_contract: getQueryParamValue(
          query,
          'sellerSignedContract',
        )[0],
      },
    }),
    push: undefined,
    set: undefined,
    update: (authUid, handover, originalHandover) => {
      if (!handover.action) {
        throw new Error('Missing action field on contest handover update.');
      }
      switch (handover.action) {
        case ContestHandoverUpdateAction.SUBMIT:
        case ContestHandoverUpdateAction.RELEASE_PRIZE:
          return {
            endpoint: `contests/0.1/handovers/${originalHandover.id}/`,
            method: 'PUT',
            payload: {
              action: handover.action,
            },
          };
        case ContestHandoverUpdateAction.SIGN_AGREEMENT:
          if (
            (toNumber(authUid) === handover.buyerId &&
              handover.buyerSignedContract) ||
            (toNumber(authUid) === handover.sellerId &&
              handover.sellerSignedContract)
          ) {
            throw new Error('User has already signed the agreement.');
          }
          if (
            !handover.extraForUpdate?.fullname ||
            !handover.extraForUpdate?.phone ||
            !handover.extraForUpdate?.address ||
            !handover.extraForUpdate?.country_code ||
            !handover.extraForUpdate?.state_code ||
            !handover.extraForUpdate?.city
          ) {
            throw new Error(
              'All fields in extraForUpdate must be filled in to sign the agreement.',
            );
          }

          return {
            endpoint: `contests/0.1/handovers/${originalHandover.id}`,
            method: 'PUT',
            payload: {
              action: handover.action,
              fullname: handover.extraForUpdate.fullname,
              phone: handover.extraForUpdate.phone,
              address: handover.extraForUpdate.address,
              country_code: handover.extraForUpdate.country_code,
              state_code: handover.extraForUpdate.state_code,
              city: handover.extraForUpdate.city,
            },
          };
        default:
          throw new Error(`Invalid action ${handover.action}`);
      }
    },
    remove: undefined,
  };
}
function getUsers(
  query: RawQuery<ContestHandover> | undefined,
): readonly number[] | undefined {
  if (!query) {
    return undefined;
  }
  const sellerIds = getQueryParamValue(query, 'sellerId');
  return sellerIds.length > 0
    ? sellerIds
    : getQueryParamValue(query, 'buyerId');
}
